import { APP_VERSION_UPDATE_DATE_KEY } from '@e3dc-react/shell/Libs/consts'
import { minutesToMilliseconds } from '@e3dc-react/shell/Libs/date'
import { useEffect } from 'react'
import { useTypedSelector } from 'src/State/RootReducer'
import { version as currentAppVersion } from '../../package.json'
import { REDIRECT_QUERY_PARAM } from 'src/Libs/navigation/consts'
import { AUTH_TOKEN_KEY, REAUTH_TOKEN_KEY } from '@e3dc-react/shell/Libs/authentication/hooks/use-authentication-provider'

// Key is also defined in clear-cache.ts
export const APP_VERSION_KEY = 'APP_VERSION'
/**
 * Clears the cache when the app version changes.
 * This is necessary to ensure that the app works correctly after an update.
 */
const useClearCacheOnVersionChange = (): void => {
  const isLoggedIn = Boolean(useTypedSelector(state => state.auth.userId))

  useEffect(() => {
    if (!isLoggedIn) return
    /* run the function on mount to not wait 1 minute before the interval runs */
    clearCacheOnVersionChange()

    const interval = setInterval(() => {
      clearCacheOnVersionChange()
    }, minutesToMilliseconds(1))

    return () => {
      clearInterval(interval)
    }
  }, [isLoggedIn])
}

export default useClearCacheOnVersionChange

const clearCacheOnVersionChange = (): void => {
  try {
    const savedAppVersion = localStorage.getItem(APP_VERSION_KEY)

    if (savedAppVersion === null || savedAppVersion !== currentAppVersion) {
      localStorage.setItem(APP_VERSION_KEY, currentAppVersion)
      localStorage.setItem(APP_VERSION_UPDATE_DATE_KEY, new Date().toISOString())
      clearCache()
    }
  } catch (error) {
    console.error('Failed to check app version:', error)
  }
}

const clearCache = (): void => {
  const keysToKeep = [AUTH_TOKEN_KEY, REAUTH_TOKEN_KEY, APP_VERSION_UPDATE_DATE_KEY, APP_VERSION_KEY, REDIRECT_QUERY_PARAM]

  Object.keys(localStorage).forEach(key => {
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key)
    }
  })

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then(registrations => {
        for (const registration of registrations) {
          registration.unregister()
        }
      })
      .catch(error => console.error('Failed to get SW registrations:', error))
  }

  window.location.reload()
}
